import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Mail, ArrowRight } from "lucide-react";
import api, { formatApiError } from "@/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import Seo from "@/components/Seo";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!email) return;
    setSubmitting(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setSent(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-md mx-auto px-4 py-16">
      <Seo title="Reset your password" description="Reset your Strategic Tracking account password."/>
      {sent ? (
        <div className="text-center" data-testid="forgot-sent">
          <div className="w-12 h-12 rounded-full bg-emerald-100 text-emerald-700 grid place-items-center mx-auto"><Mail size={20}/></div>
          <h1 className="font-display font-extrabold text-2xl text-slate-900 mt-4">Check your inbox</h1>
          <p className="mt-3 text-sm text-slate-600 leading-relaxed">
            If an account exists for <span className="font-mono">{email}</span>, we’ve sent a one-time reset link.
            The link expires in 24 hours. Check spam if it doesn’t arrive within a couple of minutes.
          </p>
          <Link to="/login" className="btn-secondary mt-6 inline-flex" data-testid="forgot-back-login">Back to sign in</Link>
        </div>
      ) : (
        <>
          <div className="text-xs font-semibold uppercase tracking-widest text-slate-500">Forgot password</div>
          <h1 className="font-display font-extrabold text-3xl text-slate-900 mt-1">Reset your password</h1>
          <p className="text-sm text-slate-500 mt-2">
            Enter the email on your Strategic Tracking account. We’ll send a one-time reset link.
          </p>

          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="forgot-form">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-widest text-slate-500 mb-1.5 block">Email</Label>
              <Input type="email" autoFocus value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="forgot-email"/>
            </div>
            <button type="submit" disabled={submitting} className="btn-primary w-full justify-center h-11" data-testid="forgot-submit">
              {submitting ? "Sending…" : "Send reset link"} <ArrowRight size={14}/>
            </button>
          </form>

          <div className="mt-6 text-sm text-slate-600">
            Remembered it? <Link to="/login" className="text-[#002FA7] font-semibold hover:underline">Sign in</Link>
          </div>
        </>
      )}
    </div>
  );
}
